import React, { useState, useRef, useEffect } from 'react';
import Styles from './VideoPlayer.module.scss';

interface VideoPlayerProps {
  url: string;
  className?: string;
  maxHeight?: string; // e.g., "35vh", "200px", "50%"
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ url, className, maxHeight = '35vh' }) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isBuffering, setIsBuffering] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<string>('16 / 9'); // Default to landscape
  const [containerStyle, setContainerStyle] = useState<React.CSSProperties>({});
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Detect video MIME type from URL
  const getVideoType = (videoUrl: string): string => {
    // Handle Firebase Storage URLs and regular URLs
    // Extract the path before query parameters
    const urlPath = videoUrl.split('?')[0];
    // Decode URL-encoded characters
    const decodedPath = decodeURIComponent(urlPath);
    // Get file extension
    const extension = decodedPath.split('.').pop()?.toLowerCase() || '';
    
    switch (extension) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'ogg':
      case 'ogv':
        return 'video/ogg';
      case 'mov':
        return 'video/quicktime';
      case 'm4v':
        return 'video/mp4';
      default:
        // If we can't detect, let browser decide
        return '';
    }
  };

  useEffect(() => {
    // Reset error state when URL changes
    setHasError(false);
    setErrorMessage('');
    setIsBuffering(false);
    setAspectRatio('16 / 9'); // Reset to default
    setContainerStyle({});
    
    // Force video reload when URL changes
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [url]);

  const handleError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const video = e.currentTarget;
    const error = video.error;
    
    if (error) {
      setHasError(true);
      let message = 'Unable to play video.';
      
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          message = 'Video loading was interrupted.';
          break;
        case error.MEDIA_ERR_NETWORK:
          message = 'Network error. Please check your connection.';
          break;
        case error.MEDIA_ERR_DECODE:
          message = 'Unable to play this video.';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message = 'This video cannot be played.';
          break;
        default:
          message = 'Unable to play this video.';
      }
      
      setErrorMessage(message);
      console.error('Video error:', error.code, message);
    }
  };

  const handleLoadedMetadata = () => {
    // Check if video has valid dimensions (detects black screen issue with unsupported formats)
    if (videoRef.current && wrapperRef.current) {
      const video = videoRef.current;
      // If video has loaded metadata but has 0x0 dimensions, it can't be displayed
      if (video.readyState >= 2 && video.videoWidth === 0 && video.videoHeight === 0) {
        setHasError(true);
        setErrorMessage('This video cannot be displayed.');
        return;
      }
      
      // Calculate and set aspect ratio based on video dimensions
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        const videoRatio = video.videoWidth / video.videoHeight;
        
        // Parse maxHeight prop (supports vh, px, %)
        let maxHeightPx: number;
        if (maxHeight.endsWith('vh')) {
          maxHeightPx = window.innerHeight * (parseFloat(maxHeight) / 100);
        } else if (maxHeight.endsWith('px')) {
          maxHeightPx = parseFloat(maxHeight);
        } else if (maxHeight.endsWith('%')) {
          const containerElement = wrapperRef.current.parentElement;
          const containerHeight = containerElement?.clientHeight || window.innerHeight;
          maxHeightPx = containerHeight * (parseFloat(maxHeight) / 100);
        } else {
          maxHeightPx = window.innerHeight * 0.35; // fallback to 35vh
        }
        
        // Get available container width
        const containerElement = wrapperRef.current.parentElement;
        const availableWidth = containerElement?.clientWidth || wrapperRef.current.offsetWidth || 600;
        const maxWidth = availableWidth;
        
        // Calculate dimensions based on width first
        let calculatedWidth = maxWidth;
        let calculatedHeight = calculatedWidth / videoRatio;
        
        // If height exceeds max-height, constrain by height and recalculate width
        if (calculatedHeight > maxHeightPx) {
          calculatedHeight = maxHeightPx;
          calculatedWidth = calculatedHeight * videoRatio;
        }
        
        // Set aspect ratio and container dimensions
        setAspectRatio(`${video.videoWidth} / ${video.videoHeight}`);
        setContainerStyle({
          width: `${calculatedWidth}px`,
          maxWidth: '100%',
          maxHeight: maxHeight, // Use the prop value directly for CSS
        });
      }
    }
    // Video metadata loaded successfully
    setHasError(false);
    setErrorMessage('');
  };

  const handleWaiting = () => {
    setIsBuffering(true);
  };

  const handleCanPlay = () => {
    setIsBuffering(false);
  };

  const handleCanPlayThrough = () => {
    setIsBuffering(false);
  };

  return (
    <div 
      ref={wrapperRef}
      className={`${Styles.videoPlayerWrapper} ${className || ''}`}
      style={{ 
        aspectRatio: aspectRatio,
        ...containerStyle
      }}
    >
      {hasError ? (
        <div className={Styles.errorContainer}>
          <div className={Styles.errorIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <p className={Styles.errorMessage}>{errorMessage}</p>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            controls={true}
            width="100%"
            height="100%"
            onError={handleError}
            onLoadedMetadata={handleLoadedMetadata}
            onWaiting={handleWaiting}
            onCanPlay={handleCanPlay}
            onCanPlayThrough={handleCanPlayThrough}
            className={Styles.videoPlayer}
            preload="auto"
            playsInline
          >
            <source src={url} type={getVideoType(url)} />
          </video>
          {isBuffering && (
            <div className={Styles.bufferingIndicator}>
              <div className={Styles.spinner}></div>
              <span className={Styles.bufferingText}>Buffering...</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VideoPlayer;

